import React, { useState, useEffect, useRef } from "react";
import { Row, Col, Card, Form, Table } from 'react-bootstrap';
import { Button } from 'primereact/button';
import { InputIcon } from "primereact/inputicon";
import { InputText } from "primereact/inputtext";
import { IconField } from "primereact/iconfield";
import { Paginator } from "primereact/paginator";
import { Toast } from "primereact/toast";
import axios from 'axios';
import config from '../config';
import { useAuth } from '../context/AuthContext';
import { toWords } from "number-to-words";
import { Sidebar } from 'primereact/sidebar';
import { Link } from 'react-router-dom'; // Assuming React Router is used

const EMPTY_FILTERS = {
    search: '',
    invoiceNo: '',
    client: '',
    description: '',
    remarks: '',
    series: '',
    financialYear: '',
    status: '',
    currency: '',
    minAmount: '',
    maxAmount: '',
    startDate: '',
    endDate: '',
    sort: 'issue_desc',
};

// Filters that live in the "More filters" panel (counted on the toggle button)
const ADVANCED_KEYS = ['invoiceNo', 'client', 'description', 'remarks', 'currency', 'minAmount', 'maxAmount', 'startDate', 'endDate'];

const currencySymbols = {
    INR: "₹ ",
    USD: "$ ",
    GBP: "£ ",
    EUR: "€ ",
    AED: "د.إ ",
    AUD: "A$ ",
    CAD: "C$ "
};

const getCurrencySymbol = (currencyCode) => {
    return currencySymbols[currencyCode] || currencyCode; // Default to currency code if not found
};

const formatAmount = (value, currencyCode) => {
    if (value === null || value === undefined || value === '') return '-';
    const number = Number(value);
    if (Number.isNaN(number)) return value;
    return `${getCurrencySymbol(currencyCode) || ''}${number.toLocaleString(currencyCode === 'INR' ? 'en-IN' : 'en-US', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    })}`;
};

const formatDate = (value) => (value ? new Date(value).toLocaleDateString('en-GB') : '-');

const getItems = (data) => {
    if (Array.isArray(data)) return data;
    if (typeof data === 'string') {
        try {
            const parsed = JSON.parse(data);
            return Array.isArray(parsed) ? parsed : [];
        } catch {
            return [];
        }
    }
    return [];
};

const pdfFileName = (invoice) => {
    const base = String(invoice.invoice || invoice.invoice_no || `Invoice_${invoice.id}`)
        .replace(/[^\w.-]+/g, '_')
        .replace(/^_+|_+$/g, '');
    const suffix = invoice.status === 2 ? '_CANCELLED' : '';
    return `${base || `Invoice_${invoice.id}`}${suffix}.pdf`;
};

const Old_invoice = () => {
    const { accessToken, userId } = useAuth();
    const toast = useRef(null);

    const [filters, setFilters] = useState(EMPTY_FILTERS);
    const [appliedFilters, setAppliedFilters] = useState(EMPTY_FILTERS);
    const [showAdvanced, setShowAdvanced] = useState(false);
    const [first, setFirst] = useState(0);
    const [rows, setRows] = useState(20);

    const [invoices, setInvoices] = useState([]);
    const [totalRecords, setTotalRecords] = useState(0);
    const [loading, setLoading] = useState(false);
    const [downloading, setDownloading] = useState({});

    const [years, setYears] = useState([]);
    const [currencies, setCurrencies] = useState([]);
    const [seriesOptions, setSeriesOptions] = useState([]);

    const [visibleRight, setVisibleRight] = useState(false);
    const [selectedInvoice, setSelectedInvoice] = useState(null);

    const updateFilter = (key, value) => setFilters((prev) => ({ ...prev, [key]: value }));
    const clearFilters = () => setFilters(EMPTY_FILTERS);

    const advancedCount = ADVANCED_KEYS.filter((key) => filters[key] !== '').length;
    const hasAnyFilter = Object.keys(EMPTY_FILTERS).some((key) => filters[key] !== EMPTY_FILTERS[key]);

    // Load dropdown options once
    useEffect(() => {
        const headers = { Authorization: `Bearer ${accessToken}` };
        axios.get(`${config.apiBASEURL}/OldInvoiceRoutes/financial-years`, { headers })
            .then((response) => setYears(response.data.data || []))
            .catch((error) => console.error("Error fetching financial years:", error));
        axios.get(`${config.apiBASEURL}/OldInvoiceRoutes/currencies`, { headers })
            .then((response) => setCurrencies(response.data.data || []))
            .catch((error) => console.error("Error fetching currencies:", error));
        axios.get(`${config.apiBASEURL}/OldInvoiceRoutes/invoice-series`, { headers })
            .then((response) => setSeriesOptions(response.data.data || []))
            .catch((error) => console.error("Error fetching invoice series:", error));
    }, [accessToken]);

    // Debounce typing; apply filters and jump back to the first page together
    useEffect(() => {
        const timer = setTimeout(() => {
            setAppliedFilters((prev) => (prev === filters ? prev : filters));
            setFirst(0);
        }, 400);
        return () => clearTimeout(timer);
    }, [filters]);

    // Fetch the current page; stale responses are cancelled
    useEffect(() => {
        const controller = new AbortController();
        const f = appliedFilters;
        setLoading(true);
        axios.get(`${config.apiBASEURL}/OldInvoiceRoutes/old-invoices`, {
            headers: { Authorization: `Bearer ${accessToken}` },
            signal: controller.signal,
            params: {
                page: Math.floor(first / rows) + 1,
                limit: rows,
                search: f.search || undefined,
                invoice_no: f.invoiceNo || undefined,
                bill_name: f.client || undefined,
                description: f.description || undefined,
                remarks: f.remarks || undefined,
                series: f.series || undefined,
                financial_year: f.financialYear || undefined,
                status: f.status || undefined,
                currency: f.currency || undefined,
                min_amount: f.minAmount || undefined,
                max_amount: f.maxAmount || undefined,
                startDate: f.startDate || undefined,
                endDate: f.endDate || undefined,
                sort: f.sort,
            },
        })
            .then((response) => {
                setInvoices(response.data.data || []);
                setTotalRecords(response.data.total || 0);
                setLoading(false);
            })
            .catch((error) => {
                if (axios.isCancel(error)) return;
                console.error("Error fetching invoices:", error);
                setInvoices([]);
                setTotalRecords(0);
                setLoading(false);
            });
        return () => controller.abort();
    }, [appliedFilters, first, rows, accessToken]);

    // PDF is streamed back by the server and saved directly
    const downloadInvoice = async (invoice) => {
        setDownloading((prev) => ({ ...prev, [invoice.id]: true }));
        try {
            const response = await axios.get(
                `${config.apiBASEURL}/OldInvoiceRoutes/old-invoices-download/${invoice.id}/${userId}`,
                {
                    headers: { Authorization: `Bearer ${accessToken}` },
                    responseType: 'blob',
                }
            );
            const url = URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
            const link = document.createElement('a');
            link.href = url;
            link.download = pdfFileName(invoice);
            document.body.appendChild(link);
            link.click();
            link.remove();
            setTimeout(() => URL.revokeObjectURL(url), 10000);
        } catch (error) {
            console.error("Error downloading invoice:", error);
            toast.current?.show({ severity: 'error', summary: 'Download failed', detail: `Could not generate PDF for ${invoice.invoice || 'this invoice'}.`, life: 4000 });
        } finally {
            setDownloading((prev) => {
                const next = { ...prev };
                delete next[invoice.id];
                return next;
            });
        }
    };

    const textFilter = (key, label, placeholder) => (
        <Form.Group>
            <Form.Label className="small text-muted mb-1">{label}</Form.Label>
            <Form.Control
                size="sm"
                value={filters[key]}
                onChange={(e) => updateFilter(key, e.target.value)}
                placeholder={placeholder}
            />
        </Form.Group>
    );

    return (
        <>
            <Toast ref={toast} />
            <Row className="mx-0 pb-3 align-items-end g-2">
                <Col lg={5} md={12} className="p-0">
                    <IconField iconPosition="left">
                        <InputIcon className="pi pi-search" />
                        <InputText
                            value={filters.search}
                            onChange={(e) => updateFilter('search', e.target.value)}
                            placeholder="Search invoice no, client, description, remarks, amount..."
                            className="border-0 border-bottom ps-5 w-100"
                        />
                    </IconField>
                </Col>
                <Col lg={7} md={12} className="d-flex flex-wrap gap-2 justify-content-lg-end justify-content-center align-items-end p-0">
                    <Form.Select
                        value={filters.series}
                        onChange={(e) => updateFilter('series', e.target.value)}
                        className="border-0 border-bottom respWidth"
                        style={{ width: "140px" }}
                        title="Invoice series (prefix of the invoice id)"
                    >
                        <option value="">All Series</option>
                        {seriesOptions.map(({ series, count }) => (
                            <option key={series} value={series}>{series} ({count})</option>
                        ))}
                    </Form.Select>
                    <Form.Select
                        value={filters.financialYear}
                        onChange={(e) => updateFilter('financialYear', e.target.value)}
                        className="border-0 border-bottom respWidth"
                        style={{ width: "150px" }}
                    >
                        <option value="">All Years</option>
                        {years.map((year) => (
                            <option key={year} value={year}>{year}</option>
                        ))}
                    </Form.Select>
                    <Form.Select
                        value={filters.status}
                        onChange={(e) => updateFilter('status', e.target.value)}
                        className="border-0 border-bottom respWidth"
                        style={{ width: "140px" }}
                    >
                        <option value="">All Status</option>
                        <option value="1">Sent</option>
                        <option value="2">Cancelled</option>
                    </Form.Select>
                    <Form.Select
                        value={filters.sort}
                        onChange={(e) => updateFilter('sort', e.target.value)}
                        className="border-0 border-bottom respWidth"
                        style={{ width: "170px" }}
                    >
                        <option value="issue_desc">Newest first</option>
                        <option value="issue_asc">Oldest first</option>
                        <option value="total_desc">Amount: high to low</option>
                        <option value="total_asc">Amount: low to high</option>
                    </Form.Select>
                    <Button
                        label={advancedCount ? `Filters (${advancedCount})` : 'Filters'}
                        icon={showAdvanced ? 'pi pi-chevron-up' : 'pi pi-filter'}
                        size="small"
                        outlined={!showAdvanced}
                        onClick={() => setShowAdvanced((prev) => !prev)}
                    />
                    {hasAnyFilter && (
                        <Button label="Clear" icon="pi pi-times" size="small" text severity="danger" onClick={clearFilters} />
                    )}
                </Col>
            </Row>

            {showAdvanced && (
                <Card className="shadow-0 border mb-3">
                    <Card.Body className="py-3">
                        <Row className="g-3">
                            <Col lg={3} md={4} sm={6}>{textFilter('invoiceNo', 'Invoice No', 'e.g. TG367/24-25')}</Col>
                            <Col lg={3} md={4} sm={6}>{textFilter('client', 'Client', 'Name, company, email, phone, GSTIN')}</Col>
                            <Col lg={3} md={4} sm={6}>{textFilter('description', 'Item Description', 'e.g. Wedding Invitation')}</Col>
                            <Col lg={3} md={4} sm={6}>{textFilter('remarks', 'Remarks', 'Invoice remarks')}</Col>
                            <Col lg={3} md={4} sm={6}>
                                <Form.Group>
                                    <Form.Label className="small text-muted mb-1">Currency</Form.Label>
                                    <Form.Select size="sm" value={filters.currency} onChange={(e) => updateFilter('currency', e.target.value)}>
                                        <option value="">All</option>
                                        {currencies.map((code) => (
                                            <option key={code} value={code}>{code}</option>
                                        ))}
                                    </Form.Select>
                                </Form.Group>
                            </Col>
                            <Col lg={3} md={4} sm={6}>
                                <Form.Label className="small text-muted mb-1">Total Amount</Form.Label>
                                <div className="d-flex gap-2">
                                    <Form.Control size="sm" type="number" min="0" placeholder="Min" value={filters.minAmount} onChange={(e) => updateFilter('minAmount', e.target.value)} />
                                    <Form.Control size="sm" type="number" min="0" placeholder="Max" value={filters.maxAmount} onChange={(e) => updateFilter('maxAmount', e.target.value)} />
                                </div>
                            </Col>
                            <Col lg={3} md={4} sm={6}>
                                <Form.Label className="small text-muted mb-1">Issue Date</Form.Label>
                                <div className="d-flex gap-2">
                                    <Form.Control size="sm" type="date" value={filters.startDate} max={filters.endDate || undefined} onChange={(e) => updateFilter('startDate', e.target.value)} />
                                    <Form.Control size="sm" type="date" value={filters.endDate} min={filters.startDate || undefined} onChange={(e) => updateFilter('endDate', e.target.value)} />
                                </div>
                            </Col>
                        </Row>
                    </Card.Body>
                </Card>
            )}

            <Card className="shadow-0 resp_report">
                <Table hover size="sm" className="table-responsive">
                    <thead className="table-primary sticky-top" style={{ top: '-35px' }}>
                        <tr>
                            <th>Invoice Id</th>
                            <th>Client</th>
                            <th style={{ width: '280px' }}>DESC</th>
                            <th style={{ width: '60px' }}>Currency</th>
                            <th>Date</th>
                            <th>Amount</th>
                            <th style={{ width: '80px' }}>Action</th>
                        </tr>
                    </thead>
                    <tbody>
                        {loading ? (
                            <tr>
                                <td colSpan="7" className="text-center">
                                    Loading...
                                </td>
                            </tr>
                        ) : invoices.length > 0 ? (
                            invoices.map((invoice) => {
                                const items = getItems(invoice.data);
                                return (
                                    <tr key={invoice.id} className={invoice.status === 2 ? "cancelled" : ""}>
                                        <td>
                                            {invoice.invoice}
                                            {invoice.status === 2 && <small className="d-block text-danger">Cancelled</small>}
                                        </td>
                                        <td>
                                            <Link to={`/Graphe/dashboard/view_client/${invoice.Client_id}`} >
                                                {invoice.clientinv?.Billing_name}
                                            </Link>
                                            <span className="d-block">
                                                <Button
                                                    className="p-button-sm p-0"
                                                    icon="pi pi-phone"
                                                    rounded
                                                    text
                                                    severity="success"
                                                    tooltip={invoice.clientinv?.phone}
                                                    tooltipOptions={{ position: "bottom", mouseTrack: true, mouseTrackTop: 15 }}
                                                />
                                                <Button
                                                    className="p-button-sm p-0"
                                                    icon="pi pi-envelope"
                                                    rounded
                                                    text
                                                    severity="primary"
                                                    tooltip={invoice.clientinv?.email}
                                                    tooltipOptions={{ position: "bottom", mouseTrack: true, mouseTrackTop: 15 }}
                                                />
                                            </span>
                                        </td>
                                        <td>
                                            {items.slice(0, 2).map((item, index) => (
                                                <p key={index} className="m-0">{item.description}</p>
                                            ))}
                                            {items.length > 2 && <p className="m-0"><small className="text-muted">+{items.length - 2} more item(s)</small></p>}
                                            {invoice.remarks && <p className="m-0"><small className="text-muted">Remarks: {invoice.remarks}</small></p>}
                                            <p className="m-0"><small className="text-muted">Template: {invoice.template_name}</small></p>
                                        </td>
                                        <td>{invoice.currency}</td>
                                        <td>
                                            <p className="m-0"><small>Issue Date:  {formatDate(invoice.issue_date)}</small> </p>
                                            <p className="m-0"><small className="text-danger">Due Date:    {formatDate(invoice.due_date)}</small></p>
                                        </td>
                                        <td>
                                            <p className="m-0"><small>Cost: </small> <b>{formatAmount(invoice.sub_total, invoice.currency)}</b></p>
                                            {invoice.lut ? (
                                                <p className="m-0"><small>LUT ARN No: </small> <b>{invoice.templateinv?.Lut_Arn_No}</b></p>
                                            ) : (
                                                <>
                                                    <p className="m-0"><small>Tax Type: </small> <b>{invoice.tax_type}</b></p>
                                                    <p className="m-0"><small>Tax: </small> <b>{formatAmount(invoice.tax, invoice.currency)}</b></p>
                                                </>
                                            )}
                                            <p className="m-0"><small>Total: </small> <b>{formatAmount(invoice.total, invoice.currency)}</b></p>
                                        </td>
                                        <td style={{ width: '80px' }}>
                                            <Button
                                                icon="pi pi-eye"
                                                rounded
                                                text
                                                severity="help"
                                                aria-label="View"
                                                tooltip="View"
                                                tooltipOptions={{ position: "bottom", mouseTrack: true, mouseTrackTop: 15 }}
                                                onClick={() => {
                                                    setSelectedInvoice(invoice); // Set the selected invoice
                                                    setVisibleRight(true); // Show the sidebar/modal
                                                }}
                                                style={{ position: 'relative', zIndex: '2' }}
                                            />
                                            <Button
                                                icon={downloading[invoice.id] ? "pi pi-spin pi-spinner" : "pi pi-download"}
                                                rounded
                                                text
                                                severity="success"
                                                aria-label="Download"
                                                tooltip={downloading[invoice.id] ? "Generating PDF..." : "Download PDF"}
                                                tooltipOptions={{ position: "bottom", mouseTrack: true, mouseTrackTop: 15 }}
                                                disabled={!!downloading[invoice.id]}
                                                onClick={() => downloadInvoice(invoice)}
                                                style={{ position: 'relative', zIndex: '2' }}
                                            />
                                        </td>
                                    </tr>
                                );
                            })
                        ) : (
                            <tr>
                                <td colSpan="7" className="text-center">
                                    No records found.
                                </td>
                            </tr>
                        )}
                    </tbody>
                </Table>
            </Card>

            {totalRecords > 0 && (
                <Paginator
                    first={first}
                    rows={rows}
                    totalRecords={totalRecords}
                    rowsPerPageOptions={[10, 20, 50, 100]}
                    onPageChange={(e) => {
                        setFirst(e.first);
                        setRows(e.rows);
                    }}
                    template="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink RowsPerPageDropdown CurrentPageReport"
                    currentPageReportTemplate="Showing {first} to {last} of {totalRecords} invoices"
                    className="pt-3"
                />
            )}

            <Sidebar visible={visibleRight} position="right" onHide={() => setVisibleRight(false)} className="resp_sidewidth" style={{ width: '50vw' }}>
                {selectedInvoice && (
                    <Card className='shadow-sm'>
                        <Card.Body className='p-0'>
                            <div className="inv-Div" style={{ position: 'relative' }}>
                                {selectedInvoice.status === 2 && (
                                    <div
                                        aria-hidden="true"
                                        style={{
                                            position: 'absolute', top: '40%', left: '50%',
                                            transform: 'translate(-50%, -50%) rotate(-35deg)',
                                            fontSize: '64px', fontWeight: 800, letterSpacing: '6px', lineHeight: 1,
                                            color: 'rgba(220, 0, 0, 0.28)', border: '5px solid rgba(220, 0, 0, 0.28)',
                                            padding: '8px 24px', borderRadius: '12px',
                                            zIndex: 5, pointerEvents: 'none', whiteSpace: 'nowrap',
                                        }}
                                    >
                                        CANCELLED
                                    </div>
                                )}
                                <div className="inv_header">
                                    <div className="logo_part">
                                        <img
                                            src={selectedInvoice.logo_url || require("../assets/images/logo.png")}
                                            alt=''
                                        />
                                    </div>
                                    <span>
                                        <b>{selectedInvoice.templateinv?.header_text}</b>
                                    </span>
                                </div>
                                <div className="inv_address">
                                    <div>
                                        <p><span>BILLED TO :</span> {selectedInvoice.clientinv?.ClientDetails?.[0]?.bill_name || "N/A"}</p>
                                        <p><span>Address :</span> {selectedInvoice.clientinv?.ClientDetails?.[0]?.bill_address || "N/A"}</p>
                                        <p><span>GSTIN / UIN :</span> {selectedInvoice.clientinv?.ClientDetails?.[0]?.gstin || "N/A"}</p>
                                        <p><span>PAN / IT NO :</span> {selectedInvoice.clientinv?.ClientDetails?.[0]?.pan || "N/A"}</p>

                                    </div>
                                    <div>
                                        <p><span>PROFORMA NO :</span> {selectedInvoice.invoice}</p>
                                        <p><span>ISSUE DATE :</span> {formatDate(selectedInvoice.issue_date)}</p>
                                        <p><span>GSTIN :</span> {selectedInvoice.templateinv?.GSTIN}</p>
                                        <p><span>PAN : </span> {selectedInvoice.templateinv?.PAN}</p>
                                        <p><span>MSME TYPE : </span> {selectedInvoice.templateinv?.MSME_Type}</p>
                                        <p><span>MSME NO : </span> {selectedInvoice.templateinv?.MSME_Number}</p>
                                        {selectedInvoice.lut && (
                                            <p><span>LUT ARN : </span> {selectedInvoice.templateinv?.Lut_Arn_No}</p>
                                        )}

                                    </div>
                                </div>
                                <div className="inv_cost">
                                    <Table bordered size="sm">
                                        <thead>
                                            <tr>
                                                <th>Description</th>
                                                <th>SAC/HSN</th>
                                                <th>QTY</th>
                                                <th className="text-end">Price</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {getItems(selectedInvoice.data).map((product, index) => (
                                                <tr key={index}>
                                                    <td style={{ whiteSpace: 'pre-line' }}>
                                                        {product.description}
                                                    </td>
                                                    <td>{product.hsn ? `HSN-${product.hsn}` : product.sac_code ? `SAC-${product.sac_code}` : '-'}</td>
                                                    <td>{product.qty ?? product.quantity ?? '-'}</td>
                                                    <td className="text-end">
                                                        <span>{formatAmount(product.price, selectedInvoice.currency)}</span>
                                                    </td>
                                                </tr>
                                            ))}
                                            <tr>
                                                <th colSpan={3} className="text-end">Sub Total</th>
                                                <td className="text-end">{formatAmount(selectedInvoice.sub_total, selectedInvoice.currency)}</td>
                                            </tr>

                                            {!selectedInvoice.lut && selectedInvoice.tax_type && (
                                                <tr>
                                                    <th colSpan={3} className="text-end">{selectedInvoice.tax_type} </th>
                                                    <td className="text-end">{formatAmount(selectedInvoice.tax, selectedInvoice.currency)}</td>
                                                </tr>
                                            )}

                                            <tr>
                                                <th colSpan={3} className="text-end">TOTAL VALUE (IN FIGURE) </th>
                                                <td className="text-end">{formatAmount(selectedInvoice.total, selectedInvoice.currency)}</td>
                                            </tr>
                                            <tr>
                                                <th colSpan={3}>TOTAL VALUE (IN WORDS) </th>
                                                <td style={{ textTransform: 'capitalize' }}>{toWords(Number(selectedInvoice.total) || 0)}</td>
                                            </tr>
                                            <tr>
                                                <th colSpan={3}>PAYMENT DUE DATE:</th>
                                                <td>{formatDate(selectedInvoice.due_date)} </td>
                                            </tr>
                                            {selectedInvoice.lut && (
                                                <tr>
                                                    <th colSpan={3}>Without Payment of Tax under LUT No.</th>
                                                    <td>{selectedInvoice.templateinv?.Lut_Arn_No}</td>
                                                </tr>
                                            )}


                                        </tbody>
                                    </Table>
                                    <div className="noteText mb-4">

                                    </div>
                                    <div className="sign_sec mb-4">
                                        <div className="note_de">
                                            <p><b>Remarks :</b></p>
                                            <p>{selectedInvoice.remarks}</p>
                                            <p className="mt-3"><b>Declaration:</b></p>
                                            <p>
                                                We declare that this document shows the actual price of the good/services described and that all particulars are true and correct.
                                            </p>
                                        </div>
                                        <div className="sig">
                                            <p><b>E. & O.E</b></p>
                                            <img
                                                src={selectedInvoice.signature_url || require("../assets/images/sign_n.png")}
                                                alt=''
                                                style={{ width: '80px', height: '80px' }}
                                            />
                                            <p><small>For THE GRAPHE</small></p>
                                        </div>
                                    </div>
                                    <div className="bankDet">
                                        <p><span className="d-block">PAYMENT INFORMATION :</span>PAY BY CHEQUE/DD/RTGS/NEFT IN FAVOUR OF {selectedInvoice.templateinv?.company_name || "N/A"} </p>
                                        <p><span className="d-block">BRANCH : </span> {selectedInvoice.templateinv?.bank_branch || "N/A"}</p>
                                        <p><span>BANK :</span> {selectedInvoice.templateinv?.bank_name || "N/A"}</p>
                                        <p><span>A/C NO  :</span> {selectedInvoice.templateinv?.account_number || "N/A"}</p>

                                        <p><span>IFSC CODE : </span> {selectedInvoice.templateinv?.ifsc_code || "N/A"}</p>
                                        <p><span>MSME NO : </span>  {selectedInvoice.templateinv?.MSME_Number || "N/A"}</p>
                                        <p><span>Swift Code : </span> {selectedInvoice.templateinv?.swift_code || "N/A"}</p>
                                    </div>
                                </div>
                                <div className="pdf-footer mt-4">
                                    <p><small>{selectedInvoice.templateinv?.company_address || "N/A"}</small></p>
                                    <p><small>Call: {selectedInvoice.templateinv?.contact_no || "N/A"} | Email: <a >{selectedInvoice.templateinv?.email || "N/A"}</a></small></p>
                                </div>
                                <div className="text-center mt-3">
                                    <Button
                                        label={downloading[selectedInvoice.id] ? "Generating PDF..." : "Download PDF"}
                                        icon={downloading[selectedInvoice.id] ? "pi pi-spin pi-spinner" : "pi pi-download"}
                                        size="small"
                                        disabled={!!downloading[selectedInvoice.id]}
                                        onClick={() => downloadInvoice(selectedInvoice)}
                                    />
                                </div>
                            </div>
                        </Card.Body>
                    </Card>
                )}
            </Sidebar>
        </>
    );
};

export default Old_invoice;
